"use client";

import { Link } from "react-router-dom";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";
import {
  Layers,
  Grid3X3,
  Accessibility,
  Zap,
  Eye,
  Repeat,
  ArrowRight,
} from "lucide-react";

const principles = [
  {
    icon: <Eye className="icon-lg" />,
    title: "Clarity First",
    description:
      "Every element should communicate its purpose clearly. Remove visual noise and focus attention on what matters.",
    guidelines: [
      "Use consistent visual hierarchy",
      "Limit color usage to convey meaning",
      "Ensure sufficient contrast for readability",
    ],
  },
  {
    icon: <Repeat className="icon-lg" />,
    title: "Consistency",
    description:
      "Similar elements should look and behave the same way. Consistency reduces cognitive load and builds trust.",
    guidelines: [
      "Reuse established patterns",
      "Follow the component library",
      "Maintain spacing and alignment rules",
    ],
  },
  {
    icon: <Accessibility className="icon-lg" />,
    title: "Accessibility",
    description:
      "Design for everyone. Accessible design is good design - it benefits all users, not just those with disabilities.",
    guidelines: [
      "WCAG 2.1 AA compliance minimum",
      "Keyboard navigation support",
      "Screen reader compatibility",
    ],
  },
  {
    icon: <Zap className="icon-lg" />,
    title: "Performance",
    description:
      "Fast interfaces feel responsive and professional. Every millisecond matters for user experience.",
    guidelines: [
      "Optimize component rendering",
      "Lazy load heavy content",
      "Use appropriate animations",
    ],
  },
];

const foundationSections = [
  {
    title: "Layout",
    description: "Scaffold pattern, page containers, and section structures",
    href: "/docs/foundations/layout",
    icon: <Layers className="icon-lg" />,
  },
  {
    title: "Grid System",
    description: "Responsive grid layouts and column structures",
    href: "/docs/foundations/grid",
    icon: <Grid3X3 className="icon-lg" />,
  },
  {
    title: "Accessibility",
    description: "WCAG compliance and inclusive design practices",
    href: "/docs/accessibility",
    icon: <Accessibility className="icon-lg" />,
  },
];

export default function DocsFoundations() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Design Foundations
            </Text>
            <Badge variant="secondary">Core</Badge>
          </div>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            The fundamental principles and patterns that guide our design system.
            Understanding these foundations helps create consistent, accessible,
            and performant interfaces.
          </Text>
        </div>
      </Section>

      {/* Design Principles */}
      <Section>
        <div className="space-y-6">
        <Text as="h2" className="text-h4 font-semibold">
          Design Principles
        </Text>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {principles.map((principle) => (
            <SectionCard key={principle.title} contentClassName="p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="text-primary">{principle.icon}</div>
                  <div className="flex flex-col gap-2">
                    <Text as="p" className="font-semibold">{principle.title}</Text>
                    <Text as="p" className="text-sm text-muted-foreground">
                      {principle.description}
                    </Text>
                  </div>
                </div>
                <ul className="space-y-2 text-sm pl-10">
                  {principle.guidelines.map((guideline, i) => (
                    <li key={i} className="flex gap-2 text-muted-foreground">
                      <div className="text-primary">-</div>
                      {guideline}
                    </li>
                  ))}
                </ul>
              </div>
            </SectionCard>
          ))}
        </div>
        </div>
      </Section>

      {/* 4-Max Rule */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>The 4-Max Rule</CardTitle>
            <CardDescription>
              A constraint that breeds consistency
            </CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="space-y-6">
          <Text className="text-muted-foreground">
            To maintain consistency, each token type has at most 4 options.
            This constraint reduces decision fatigue and ensures visual harmony.
          </Text>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
              <Text className="font-medium text-sm">Spacing</Text>
              <div className="flex gap-1">
                <Badge variant="outline">sm</Badge>
                <Badge variant="outline">md</Badge>
                <Badge variant="outline">lg</Badge>
                <Badge variant="outline">xl</Badge>
              </div>
            </div>
            <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
              <Text className="font-medium text-sm">Radius</Text>
              <div className="flex gap-1">
                <Badge variant="outline">sm</Badge>
                <Badge variant="outline">md</Badge>
                <Badge variant="outline">lg</Badge>
                <Badge variant="outline">full</Badge>
              </div>
            </div>
            <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
              <Text className="font-medium text-sm">Shadows</Text>
              <div className="flex gap-1">
                <Badge variant="outline">sm</Badge>
                <Badge variant="outline">md</Badge>
                <Badge variant="outline">lg</Badge>
                <Badge variant="outline">xl</Badge>
              </div>
            </div>
            <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
              <Text className="font-medium text-sm">Duration</Text>
              <div className="flex gap-1">
                <Badge variant="outline">fast</Badge>
                <Badge variant="outline">normal</Badge>
                <Badge variant="outline">slow</Badge>
                <Badge variant="outline">none</Badge>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Foundation Sections */}
      <Section>
        <div className="space-y-6">
        <Text as="h2" className="text-h4 font-semibold">
          Explore Foundations
        </Text>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {foundationSections.map((section) => (
            <Link key={section.href} to={section.href}>
              <SectionCard contentClassName="p-6 hover:bg-accent/30 transition-colors cursor-pointer h-full">
                <div className="space-y-3">
                  <div className="text-primary">{section.icon}</div>
                  <Text className="font-semibold">{section.title}</Text>
                  <Text className="text-sm text-muted-foreground">
                    {section.description}
                  </Text>
                  <div className="flex items-center gap-1 text-primary text-sm">
                    Learn more <ArrowRight className="icon-xs" />
                  </div>
                </div>
              </SectionCard>
            </Link>
          ))}
        </div>
        </div>
      </Section>

      {/* Style Drivers */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Style Drivers</CardTitle>
            <CardDescription>
              Centralized styling through componentStyles
            </CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <Text className="text-muted-foreground">
          All component styles flow from a single source of truth -
          componentStyles.ts. This ensures consistency and makes theming
          straightforward.
        </Text>
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
          <code className="block text-muted-foreground">
            {`// componentStyles.ts - Single source of truth
export const styleDrivers = {
  button: {
    base: "inline-flex items-center justify-center rounded-md transition-colors",
    variants: {
      solid: "bg-primary text-primary-foreground hover:bg-primary/90",
      outline: "border border-input hover:bg-accent",
      text: "hover:bg-accent hover:text-accent-foreground",
    },
    sizes: {
      sm: "h-8 px-3 text-xs",
      md: "h-10 px-4 text-sm",
      lg: "h-12 px-6 text-base",
    },
  },
  // ... other components
};`}
          </code>
        </div>
      </SectionCard>
      </Section>
    </>
  );
}
